import type { RigidPose } from './localizerPose';
import { IMAGE_LOCALIZER_KIND, type PlacementPose, type ScenePlacement } from '../cms/localizers';

export interface LocalizerSession {
    selected: ScenePlacement;
    authoredPose: PlacementPose;
}
export type LocalizerSelection =
    | { status: 'selected'; session: LocalizerSession }
    | { status: 'choice-required' | 'unavailable'; reason: string };

function unavailable(p: ScenePlacement): string | null {
    if (!p.enabled) return 'This marker is disabled.';
    const localizer = p.localizer;
    if (!localizer) return 'This marker has no localizer.';
    if (localizer.kind !== IMAGE_LOCALIZER_KIND) return 'This localizer type is not supported.';
    if (p.rotationSource === 'legacy_heuristic') return 'This marker transform has not been resolved.';
    const width = localizer.widthM;
    if (!localizer.imageUrl || typeof width !== 'number' || !Number.isFinite(width) || width <= 0) {
        return 'This marker needs an image and a physical width.';
    }
    if (!validPose(p)) return 'This marker transform is invalid.';
    return null;
}
function validPose(p: PlacementPose): boolean {
    return Array.isArray(p.t) && p.t.length === 3
        && Array.isArray(p.q) && p.q.length === 4
        && [...p.t, ...p.q].every(Number.isFinite)
        && Math.abs(Math.hypot(...p.q) - 1) < 0.001;
}
/** Discovery only. This list must never be passed to a platform tracker. */
export function trackableImageLocalizers(placements: readonly ScenePlacement[]): ScenePlacement[] {
    return placements.filter(p => !unavailable(p));
}
/** Resolve session choice independently of the authored primary/content frame. */
export function resolveLocalizerSelection(placements: readonly ScenePlacement[], selectedPlacementId?: string): LocalizerSelection {
    const eligible = trackableImageLocalizers(placements);
    let selected: ScenePlacement | undefined;
    if (selectedPlacementId !== undefined) {
        selected = placements.find(p => p.id === selectedPlacementId);
        if (!selected) return { status: 'unavailable', reason: 'The selected marker does not exist.' };
        const reason = unavailable(selected);
        if (reason) return { status: 'unavailable', reason };
    } else {
        selected = eligible.find(p => p.isPrimary) ?? (eligible.length === 1 ? eligible[0] : undefined);
        if (!selected) return eligible.length > 1
            ? { status: 'choice-required', reason: 'Choose a marker before entering AR.' }
            : { status: 'unavailable', reason: 'No usable marker is available.' };
    }
    const primary = placements.find(p => p.isPrimary);
    if (!primary || primary.rotationSource === 'legacy_heuristic' || !validPose(primary)) {
        return { status: 'unavailable', reason: 'The authored content frame is unavailable.' };
    }
    return {
        status: 'selected',
        session: {
            selected: structuredClone(selected),
            authoredPose: { t: [...primary.t], q: [...primary.q] },
        },
    };
}
export function requireLocalizerSession(placements: readonly ScenePlacement[], selectedPlacementId?: string): LocalizerSession {
    const result = resolveLocalizerSelection(placements, selectedPlacementId);
    if (result.status !== 'selected') throw new Error(result.reason);
    return result.session;
}
export const LEGACY_IMAGE_LOCALIZER_ID = 'legacy-image';
export function legacyImageLocalizer(imageUrl: string, widthM: number): ScenePlacement {
    return {
        id: LEGACY_IMAGE_LOCALIZER_ID,
        localizerId: LEGACY_IMAGE_LOCALIZER_ID,
        t: [0, 0, 0],
        q: [0, 0, 0, 1],
        rotationSource: 'explicit',
        isPrimary: true,
        enabled: true,
        localizer: { id: LEGACY_IMAGE_LOCALIZER_ID, kind: IMAGE_LOCALIZER_KIND, name: '', imageUrl, widthM, displayAssetId: null },
    };
}

type XYZ = { readonly x: number; readonly y: number; readonly z: number };
type XYZW = XYZ & { readonly w: number };

/**
 * WebXR: native image-space pose (right-handed, relative to the base
 * reference space) → the anchor frame the scene is placed in.
 *
 * This is exactly the correction `XRManager` has always applied before
 * `getOffsetReferenceSpace`: `q · Ry(π)`, i.e. `(−qz, qw, qx, −qy)`, with
 * the position untouched.
 */
export function webxrImagePoseToLocalizer(p: XYZ, q: XYZW): RigidPose {
    return { t: [p.x, p.y, p.z], q: [-q.z, q.w, q.x, -q.y] };
}
